import {
  getExecutionRulesHistorySummary,
  getDownloadRulesHistory,
  getDownloadExecutionsHistory,
  getDownloadSummaryOutput,
  getDownloadDetailedOutput,
  getDownloadValidateSummaryOutput,
  getDownloadValidateDetailedOutput,
} from "./constants";
import base from "../middlewares/interceptors";
import { downloadExcelFileFromUTF } from "./utils";

export interface IExecutionHistoryData {
  page: number;
  pSize: number;
  domainId?: number | null;
  ruleId?: number | null;
  fromDate?: string | undefined;
  searchFilterData: any;
}

export const getExecutionHistory = async ({
  page,
  pSize,
  domainId,
  ruleId,
  fromDate,
  searchFilterData,
}: IExecutionHistoryData) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;
    const allFilterData = {
      ...searchFilterData,
      ...(domainId && { domain_id: domainId }),
      ...(ruleId && { rule_id: ruleId }),
      ...(fromDate && { date: fromDate }),
    };
    const params = new URLSearchParams({
      page: pageN,
      size: pageS,
      fields:
        "id,name,rule_id,domain_id,domain_name,execution_time,run_id,run_name,execution_sub_group_id,execution_sub_group_code,job_status,execution_name,execution_mode,output_logs,score,score_calculated_on",
      ...Object.entries(allFilterData || {}).reduce(
        (acc: any, [key, value]: any) => {
          if (value !== null && value !== undefined && value !== "") {
            // Transform start_date to date for URL parameters
            const paramKey = key === "start_date" ? "date" : key;
            acc[paramKey] = value;
          }
          return acc;
        },
        {},
      ),
    });
    const response = await base.get(
      `${getExecutionRulesHistorySummary}?${params.toString()}`,
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
};

export const downloadRuleHistory = async ({ searchFilterData }: any) => {
  try {
    let url = `${getDownloadRulesHistory}?`;
    searchFilterData &&
      Object.keys(searchFilterData).length > 0 &&
      Object.entries(searchFilterData).forEach(([key, value]: any) => {
        if (key && value) {
          const trimmedValue = value?.trim();
          if (key === "domain_id") {
            url += `domain_id=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "domain_name") {
            url += `domain_name=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "rule_id") {
            url += `rule_id=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "rule_name") {
            url += `rule_name=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "run_id") {
            url += `run_id=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "run_name") {
            url += `run_name=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "date" && trimmedValue) {
            url += `date=${encodeURIComponent(trimmedValue)}&`;
          }
        }
      });
    const response = await base.get(url, {
      responseType: "blob",
    });
    const fileName = "rule_history_output";
    if (response.status >= 200 && response.status < 300) {
      downloadExcelFileFromUTF(response, fileName);
    } else {
      console.error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

// this function is used for execution history download button
export const downloadExecutionHistory = async ({ searchFilterData }: any) => {
  try {
    let url = `${getDownloadExecutionsHistory}?`;
    searchFilterData &&
      Object.keys(searchFilterData).length > 0 &&
      Object.entries(searchFilterData).forEach(([key, value]: any) => {
        if (key && value) {
          const trimmedValue = value?.trim();
          if (key === "domain_id") {
            url += `domain_id=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "domain_name") {
            url += `domain_name=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "resource_id") {
            url += `resource_id=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "resource_name") {
            url += `resource_name=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "run_id") {
            url += `run_id=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "run_name") {
            url += `run_name=${encodeURIComponent(trimmedValue)}&`;
          }
          if (key === "date" && trimmedValue) {
            url += `date=${encodeURIComponent(trimmedValue)}&`;
          }
        }
      });

    const response = await base.get(url, {
      responseType: "blob",
    });
    const fileName = "validation_history_output";
    if (response.status >= 200 && response.status < 300) {
      downloadExcelFileFromUTF(response, fileName);
    } else {
      console.error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

// this function is used for summary output download button
export const downloadSummaryOutput = async (
  run_name: string | null,
  run_date: string | null,
) => {
  try {
    const response = await base.get(
      `${getDownloadSummaryOutput}/${run_name}/${run_date}`,
      { responseType: "blob" },
    );
    const fileName = "summary_output_history";
    if (response.status >= 200 && response.status < 300) {
      downloadExcelFileFromUTF(response, fileName);
    } else {
      console.error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

// this function is used for detailed output download button
export const downloadDetailedOutput = async (
  run_name: string | null,
  run_date: string | null,
) => {
  try {
    const response = await base.get(
      `${getDownloadDetailedOutput}/${run_name}/${run_date}`,
      { responseType: "blob" },
    );
    const fileName = "detailed_output_history";
    if (response.status >= 200 && response.status < 300) {
      downloadExcelFileFromUTF(response, fileName);
    } else {
      console.error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

// this function is used for validate summary output download button
export const downloadValidateSummaryOutput = async (
  run_name: string | null,
  run_date: string | null,
) => {
  try {
    const response = await base.get(
      `${getDownloadValidateSummaryOutput}/${run_name}/${run_date}`,
      { responseType: "blob" },
    );
    const fileName = "validation_summary_output_history";
    if (response.status >= 200 && response.status < 300) {
      downloadExcelFileFromUTF(response, fileName);
    } else {
      console.error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

// this function is used for validate detailed output download button
export const downloadValidationDetailedOutput = async (
  run_name: string | null,
  run_date: string | null,
) => {
  try {
    const response = await base.get(
      `${getDownloadValidateDetailedOutput}/${run_name}/${run_date}`,
      { responseType: "blob" },
    );
    const fileName = "validation_detailed_output_history";
    if (response.status >= 200 && response.status < 300) {
      downloadExcelFileFromUTF(response, fileName);
    } else {
      console.error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};
